/**
 * SIKB / TerraIndex codelists — single source of truth for the whole monorepo.
 * Each map is label (Dutch human name) → SIKB code string.
 */

/**
 * Hoofdgrondsoorten (NEN 5104) — de lijst waaruit gekozen kan worden.
 *
 * 'keien' (E) stond hier tot september 2026 in, overgenomen uit de oorspronkelijke codelijst
 * en nooit tegen een echte .ti gehouden. TerraIndex kent keien niet als grondsoort: het is
 * een fractie >63 mm en hoort in de bijzondere bestanddelen, waar hij ook al stond
 * (BZB_TYPE, 'keien' → 'KI'). Omdat `sikb_id` bij export ongewijzigd in LaSoilTypeCode
 * belandt, leverde een keien-laag een code op die TerraIndex bij import laat vallen.
 *
 * Alleen de keuze is weg, niet de code: zie BODEMSOORT_HISTORISCH.
 */
export const BODEMSOORT = new Map<string, string>([
  ['grind', 'G'],
  ['klei', 'K'],
  ['leem', 'L'],
  ['slib', 'S'],
  ['veen', 'V'],
  ['water', 'W'],
  ['zand', 'Z'],
]);

/**
 * BODEMSOORT plus codes die niet meer gekozen kunnen worden, uitsluitend voor code → label.
 *
 * Lagen van vóór de wijziging hebben 'E' als soilType in de database staan. Die moeten
 * gewoon 'keien' blijven tonen in plaats van een kale letter; wat er ooit is opgenomen
 * mag niet onleesbaar worden doordat de keuzelijst krimpt. Gebruik deze map voor weergave,
 * BODEMSOORT voor keuzelijsten, invoer en AI-enums.
 */
export const BODEMSOORT_HISTORISCH = new Map<string, string>([
  ...BODEMSOORT,
  ['keien', 'E'],
]);

export const TOEVOEGING = new Map<string, string>([
  ['grindig', 'G'],
  ['humeus', 'H'],
  ['kleiig', 'K'],
  ['keiig', 'E'],
  ['organisch', 'R'],
  ['siltig', 'S'],
  ['zandig', 'Z'],
  ['venig', 'V'],
]);

export const GRADATIE = new Map<string, string>([
  ['zwak', '1'],
  ['matig', '2'],
  ['sterk', '3'],
  ['uiterst', '4'],
]);

export const MEDIAAN = new Map<string, string>([
  ['uiterst fijn', '1'],
  ['zeer fijn', '2'],
  ['fijn', '3'],
  ['matig fijn', '3'],
  ['middelgrof', '4'],
  ['matig grof', '4'],
  ['grof', '5'],
  ['zeer grof', '5'],
  ['uiterst grof', '6'],
  ['extreem grof', '6'],
]);

export const KLEUR = new Map<string, string>([
  ['roze', '17'],
  ['beige', 'BE'],
  ['blauw', 'BL'],
  ['bruin', 'BR'],
  ['creme', 'CR'],
  ['geel', 'GE'],
  ['groen', 'GN'],
  ['grijs', 'GR'],
  ['kleurloos', 'KL'],
  ['olijf', 'OL'],
  ['oranje', 'OR'],
  ['paars', 'PA'],
  ['rood', 'RO'],
  ['roest', 'RS'],
  ['transparant', 'TR'],
  ['wit', 'WI'],
  ['zwart', 'ZW'],
]);

export const KLEUR_STERKTE = new Map<string, string>([
  ['licht', 'LI'],
  ['donker', 'DR'],
  ['neutraal', 'NE'],
  ['zeer donker', 'ZE'],
  ['zeer licht', 'ZL'],
]);

export const GEUR_TYPE = new Map<string, string>([
  ['aardgas', 'AG'], ['rook', 'RK'], ['muffe', 'MU'], ['rottings', 'RO'],
  ['veen', 'VE'], ['slib', 'SB'], ['riool', 'RI'], ['teer', 'TE'],
  ['brandstof', 'BR'], ['olie', 'OL'], ['benzine', 'BE'], ['diesel', 'DI'],
  ['zoete', 'ZO'], ['zure', 'ZU'], ['scherpe', 'SG'], ['chemisch', 'CM'],
  ['onbekende', 'ON'], ['verdachte', 'VD'], ['stookolie', 'ST'],
  ['chloor', 'CH'], ['ammoniak', 'AM'], ['oplosmiddel', 'OP'],
  ['thinner', 'TH'], ['verf', 'VF'], ['petroleum', 'PE'],
  ['carbolineum', 'CA'], ['huisbrandolie', 'HB'], ['aromaten', 'AR'],
  ['condensaat', 'CO'], ['gemengde', 'GE'], ['glycol', 'GL'],
  ['kerosine', 'KE'], ['h2s', 'HS'], ['minerale olie', 'MI'],
  ['motorolie', 'MO'], ['mest', 'MT'], ['olie aromaten', 'OA'],
  ['pesticide', 'PS'], ['niks aangetroffen', 'NA'], ['koolwaterstof', 'KW'],
  ['organisch materiaal', 'OM'], ['zeep', 'ZE'], ['weeïge', 'WE'],
]);

export const GEUR_STERKTE = new Map<string, string>([
  ['geen', '0'], ['zwak', '1'], ['matig', '2'], ['sterk', '3'], ['uiterst', '4'],
]);

/**
 * @deprecated Voor het "type onderzoek" van een boring: gebruik `BORING_TYPE`. Deze map was de
 * tweede, afwijkende lijst voor hetzelfde veld. Blijft staan voor `getMeetpuntTypeLabel` en de
 * `tiCodelists`-vorm.
 */
export const MEETPUNT_TYPE = new Map<string, string>([
  ['Boring', 'BO'],
  ['Graafgat', 'GG'],
  ['Sondering', 'SO'],
  ['Watermonster', 'WM'],
]);

export const BOORSYSTEEM = new Map<string, string>([
  ['edelman', 'ED'],
  ['avegaar', 'AV'],
  ['graven', 'GA'],
  ['holle avegaar', 'AH'],
  ['steekbus', 'ST'],
  ['zuigerboor', 'ZU'],
]);

export const MAAIVELD_TYPE = new Map<string, string>([
  ['klinker', 'KL'],
  ['gras', 'GS'],
  ['grind', 'GR'],
  ['braak', 'BR'],
  ['berm', 'BM'],
  ['tegel', 'TE'],
  ['beton', 'BE'],
  ['asfalt', 'AS'],
]);

export const BZB_TYPE = new Map<string, string>([
  // Natural/organic
  ['planten', 'PL'], ['hout', 'HO'], ['slib', 'SB'], ['oer', 'OE'],
  ['schelpen', 'SC'], ['riet', 'RI'], ['veen', 'VE'], ['humus', 'HM'],
  ['teelaarde', 'TA'], ['detritus', 'DE'], ['dy', 'DY'],
  ['plantenresten', 'PR'], ['plantenresten houtig', 'PH'],
  ['plantenresten niet-houtig', 'PN'], ['Boomwortels', 'HW'],
  ['wortels', 'WO'], ['worteldoek', 'WD'], ['Japanse Duizendknoop', '308'],
  ['bladafval', 'BV'], ['Organische stof', '312'],
  // Mineral/rock
  ['grind', 'GR'], ['keien', 'KI'], ['keileem', 'KE'], ['kalksteen', 'KS'],
  ['krijt', 'KR'], ['klei', 'KL'], ['leem', 'LE'], ['loss', 'LO'],
  ['potklei', 'PO'], ['kattenklei', 'KT'], ['siltig zand', 'LZ'],
  ['zand', 'ZA'], ['flugsand', 'FS'], ['gestabiliseerd zand', 'SZ'],
  ['vormzand', 'VZ'], ['goethietzand', 'GO'], ['gravel', 'GV'],
  ['gyttja', 'GY'], ['silex', 'SX'], ['vuursteen', 'VS'],
  ['kiezel', 'KZ'], ['split', 'SP'], ['steenslag', '310'],
  ['schist', '311'], ['leisteen', 'LS'], ['steen', 'EN'],
  ['stenen', 'ST'], ['blokken', 'BK'], ['Stenen >50 mm', '305'],
  ['vast gesteente', 'VG'], ['rotsbodem', 'RB'], ['schalie', 'SH'],
  ['siltsteen', 'SE'], ['moddersteen', 'MS'],
  ['zandsteen', 'ZS'], ['ijzerzandsteen', 'IZ'], ['mergel', 'MG'], ['lavalith', 'LL'],
  ['ligniet', 'LI'], ['leer', 'LR'], ['doleriet', 'DO'],
  ['porfier', 'PF'], ['basalt', 'B7'], ['koraalsteen', 'K3'],
  ['gips', 'GI'], ['gley', 'GL'], ['glauconiet', 'GC'],
  ['oker', 'OK'], ['Kalknodules', '303'], ['Kalkkorsten', '304'],
  // Iron/mineral
  ['ijzer', 'YZ'], ['ijzeroer', 'YO'],
  ['ijzerconcreties', 'YC'], ['ijzersulfide', 'YS'],
  ['donkere mineralen', 'DM'], ['glimmer', 'GM'],
  // Anthropogenic — construction/fill
  ['puin', 'PU'], ['metselpuin', 'MP'], ['puingranulaat', 'PG'],
  ['mengpuin', '309'], ['Bouw- en sloop afval', '302'],
  ['baksteen', 'BA'], ['bewerkt steen', 'BS'], ['klinkers', 'KK'],
  ['kassei', 'KN'], ['betonstraatstenen', 'B5'], ['gebakkenstraatstenen', 'G5'],
  ['tegel', 'TG'], ['stelcon', 'SN'], ['beton', 'BE'],
  ['beton ongebroken', 'BB'], ['betongranulaat', 'BG'],
  ['metselwerkgranulaat', 'M8'], ['menggranulaat', 'M7'],
  ['recyclinggranulaat', 'RG'], ['keramiek', 'KM'],
  ['keramische draineerbuis', 'KB'], ['gresbuis', 'GB'],
  ['zandcement', 'ZC'], ['Cement', '313'], ['Geroerd', '314'],
  ['verharding', 'VH'], ['verhardingsmateriaal', 'VM'],
  ['ophoogmateriaal', 'OM'], ['vullaag', 'VU'],
  ['conglomeraat', 'CG'], ['breccie', 'BC'], ['natuursteen', 'NS'],
  // Contaminants / waste
  ['kalk', 'KA'], ['asbest', 'AB'], ['asbestverdacht materiaal', 'AT'],
  ['niet-asbestverdacht materiaal', 'NA'], ['afval', 'AF'],
  ['organisch afval', 'OA'], ['plastic afval', 'PA'],
  ['metaal afval', 'MA'], ['banden', 'BN'], ['rubber', 'RU'],
  ['textiel', 'TX'], ['folie', 'FL'], ['piepschuim', 'PM'],
  ['plastic', 'PC'], ['glas', 'GS'], ['metaal', 'ME'],
  ['verf', 'VF'], ['olie', 'OF'], ['teer', 'TE'],
  ['bitumen', 'BI'], ['roest', 'RO'], ['sintels', 'SI'],
  ['koolas', 'AK'], ['vliegas', 'VL'], ['hoogovenslakken', 'H1'],
  ['AVIslak', 'AV'], ['slakken', 'SL'], ['gepenetreerde slakken', 'GE'],
  ['gestabiliseerde slakken', '315'], ['gestabiliseerd menggranulaat', 'SM'],
  ['mijnsteen', 'MI'], ['aardewerk', 'AA'], ['kolen', 'KO'],
  ['kolengruis', 'KG'], ['bruinkool', 'BR'], ['houtskool', 'HK'],
  ['huisvuil', 'HU'], ['munitie', 'MU'], ['fosfaat', 'FF'],
  ['fosforgips', 'FO'], ['gipspleister', 'GP'], ['erts', 'ER'],
  ['vulkanische as', 'VA'], ['brekerzand', 'BZ'],
  ['verbrand bot', 'VB'], ['verbrande klei', 'VK'],
  ['verbrandingsresten', 'VR'], ['Fijne verbrandingsresten', '299'],
  ['Middelgrove verbrandingsresten', '300'], ['grove verbrandingsresten', '301'],
  ['coproliet', 'CO'], ['bot', 'BO'],
  // Rail/ballast
  ['ballast', 'BL'], ['ballast fractie 30/63mm', 'BD'],
  ['ballast fractie 22/40mm', 'BT'], ['ballast fractie 31,5/50 mm', 'BF'],
  // Asphalt types
  ['asfalt', 'AS'], ['basaltasfalt', 'T7'], ['gietasfalt', 'GA'],
  ['zandasfalt', 'T6'], ['strijkasfalt', 'T5'], ['fijn asfalt', 'R2'],
  ['gedesintegreerd asfalt', 'R3'], ['frees asfalt', '306'],
  ['asfaltgranulaat', '307'], ['slijtlaag', 'T1'], ['rode slijtlaag', 'T2'],
  ['dunne deklaag', 'T3'], ['slemlaag', 'T4'], ['kleeflaag', 'T8'],
  ['microdeklaag', 'MD'], ['Microville', 'M1'], ['Stabiflex', 'SF'],
  ['oppervlakbehandeling', 'B4'], ['penetratielaag', 'P1'],
  ['penetratie-achtig', 'P2'], ['stol', 'SO'], ['repac', 'RP'],
  ['brac', 'B6'], ['Bims', 'BM'],
  // DAB
  ['DAB', 'D0'], ['DAB 0/6', 'D1'], ['DAB 0/8', 'D2'], ['DAB 0/11', 'D3'],
  ['DAB 0/16', 'D4'], ['DAB 0/6-0/8', 'D5'], ['DAB 0/8-0/11', 'D6'],
  ['DAB 0/11-0/16', 'D7'],
  // OAB
  ['OAB', 'O0'], ['OAB 0/8', 'O1'], ['OAB 0/11', 'O2'],
  ['OAB 0/16', 'O3'], ['OAB 0/22', 'O4'], ['OAB 0/8-0/11', 'O5'],
  ['OAB 0/11-0/16', 'O6'], ['OAB 0/16-0/22', 'O7'],
  ['OAB 0/16 type 1', 'O8'], ['OAB 0/16 type 2', 'O9'], ['OAB 0/16 type 3', 'B1'],
  // GAB
  ['GAB', 'G0'], ['GAB 0/8', 'G4'], ['GAB 0/16', 'G1'],
  ['GAB 0/32', 'G2'], ['GAB 0/16-0/32', 'G3'],
  // SMA
  ['SMA', 'S0'], ['SMA 0/5', 'S1'], ['SMA 0/8', 'S2'], ['SMA 0/11', 'S3'],
  ['SMA 0/6-0/8', 'S4'], ['SMA 0/8-0/11', 'S5'],
  ['SMA 0/11 type 1', 'B2'], ['SMA 0/11 type 2', 'B3'],
  ['SMA-NL 5', 'M2'], ['SMA-NL 8', 'M3'], ['SMA-NL 11', 'M4'],
  ['SMA-NL 5-8', 'M5'], ['SMA-NL 8-11', 'M6'],
  // ZOAB
  ['ZOAB', 'Z0'], ['ZOAB 0/8', 'Z1'], ['ZOAB 0/11', 'Z2'],
  ['ZOAB 0/16', 'Z3'], ['ZOAB fijn', 'Z4'], ['ZOAB 11', '1Z'],
  ['ZOAB 16', '2Z'], ['ZOAB 11-16', '3Z'],
  // STAB
  ['STAB', 'X0'], ['STAB 0/16', 'S7'], ['STAB 0/22', 'S8'],
  ['STAB 0/16-0/22', 'S9'],
  // EAB
  ['EAB', 'E0'], ['EAB 0/3', 'E1'], ['EAB 0/6', 'E2'], ['EAB 0/8', 'E3'],
  // WAB
  ['WAB', 'W0'], ['WAB 0/16', 'W1'], ['WAB 0/22', 'W2'],
  // Combi/ZOAB-fijn
  ['combi deklaag 0/8', 'Z5'], ['combi deklaag 0/11', 'Z6'],
  ['combi deklaag 0/16', 'Z7'],
  // Novachip
  ['Novachip 2/6', 'N1'], ['Novachip 4/8', 'N2'], ['Novachip 8/11', 'N3'],
  // AC
  ['AC 16 base', 'A1'], ['AC 22 base', 'A2'], ['AC 32 base', 'A3'],
  ['AC 16-22 base', 'A4'], ['AC 22-32 base', 'A5'],
  ['AC 11 bind', 'A6'], ['AC 16 bind', 'A7'], ['AC 22 bind', 'A8'],
  ['AC 11-16 bind', 'A9'], ['AC 16-22 bind', 'C1'],
  ['AC 8 surf', 'C2'], ['AC 11 surf', 'C3'], ['AC 16 surf', 'C4'],
  ['AC 8-11 surf', 'C5'], ['AC 11-16 surf', 'C6'],
  // Unknown asphalt types
  ['? Asfalt', 'R1'], ['? 0/6', 'R4'], ['? 0/8', 'R5'],
  ['? 0/11', 'R6'], ['? 0/16', 'R7'], ['? 0/22', 'R8'],
  // Misc combinations
  ['puin/slakken', 'PS'], ['puin/zand', 'PZ'], ['slakken/zand', 'SK'],
  ['houten vloer', 'HV'], ['koud asfalt 0/11', 'K1'], ['koud asfalt 0/16', 'K2'],
  ['turf', 'TU'], ['dakpan', 'DP'], ['zinkassen', 'ZI'],
  ['kunststofvezels', 'KV'], ['kooldeeltjes', 'KD'],
  // Natural features / water
  ['waterbodem', 'WB'], ['Water', 'WA'], ['lucht', 'LU'],
  // Misc
  ['onbekend', 'XX'],
]);

export const BZB_GRADATION = new Map<string, string>([
  ['zwak', '1'], ['matig', '2'], ['sterk', '3'], ['uiterst', '4'],
  ['volledig', '5'], ['sporen', '6'], ['resten', '7'], ['brokken', '8'],
  ['laagjes', '9'], ['lensjes', '10'], ['insluitingen', '11'],
  ['spikkels', '12'], ['veel', '13'], ['weinig', '14'],
  ['geen', '15'], ['Een beetje', '16'], ['zeer weinig', '17'],
  ['dominant', '18'], ['overvloedig', '19'],
]);

/**
 * Intensiteiten waarbij het bestanddeel de laag ÍS.
 *
 * Een laag van lucht, ballast of puin heeft geen grondsoort en hoort die ook niet te krijgen;
 * de melding 'zonder grondsoort' stond daar te knipperen bij iets wat gewoon klopt.
 *
 * NEN 6693 noemt dat 'overvloedig' (50 tot 80 % volume) en 'dominant' (80 tot 100 %); NEN 5104
 * 'uiterst' en 'volledig'. Beide staan erin, en beide vormen: 6693 bewaart de term, 5104 het
 * nummer uit deze codelijst (uiterst=4, volledig=5, dominant=18, overvloedig=19).
 */
export const BZB_FILLS_LAYER: ReadonlySet<string> = new Set([
  'overvloedig', 'dominant', 'volledig', 'uiterst',
  '4', '5', '18', '19',
]);

/** Bestaat een laag zelf uit dit bestanddeel? Dan hoort er geen grondsoort bij. */
export function bzbFillsLayer(gradation: string | null | undefined): boolean {
  return BZB_FILLS_LAYER.has(String(gradation ?? '').trim().toLowerCase());
}

export const BZB_ORIGIN = new Map<string, string>([
  ['antropogeen', 'AN'],
  ['gebiedseigen', 'GE'],
]);

// ─── Peilbuis codelists (TerraIndex FtMaterialTypeCode / FtFlowTypeCode / FtReferencePointTypeCode) ───

/** FtMaterialTypeCode — peilbuis tube material */
export const PEILBUIS_MATERIAAL = new Map<string, string>([
  ['PVC', '1'],
  ['HDPE', '2'],
  ['RVS', '3'],
  ['Anders', '4'],
  ['Stalen pijp', '5'],
  ['Waterdicht materiaal', '6'],
]);

/** FtFlowTypeCode — filter doorloop (permeability) */
export const PEILBUIS_DOORLOOP = new Map<string, string>([
  ['Geen', 'E'],
  ['Snel', 'S'],
  ['Traag', 'T'],
  ['Matig', 'M'],
  ['Slecht', 'L'],
  ['Goed', 'G'],
]);

/** FtReferencePointTypeCode — reference level for filter depth */
export const PEILBUIS_REFERENTIEVLAK = new Map<string, string>([
  ['BOPB', 'BO'],
  ['Putdeksel', 'PU'],
  ['Anders', 'AN'],
  ['Maaiveld', 'MA'],
]);

/** FtRefTopCode — reference level for top of tube (BOPB) */
export const PEILBUIS_REF_TOP = new Map<string, string>([
  ['Maaiveld', 'MA'],
  ['Referentievlak meetpunt', 'ME'],
]);

// ── Grondmonster: verpakkingstype (TI JrPackingTypeCode) ────────────────────
/**
 * Verpakking van een grondmonster. TerraIndex kent 96 opties; dit is de gecureerde
 * selectie die in het Nederlandse veldwerk daadwerkelijk wordt gebruikt, in de volgorde
 * waarin een veldwerker ze nodig heeft — de default (glazen pot 580 ml) staat bovenaan,
 * zodat er zelden gescrold hoeft te worden. `group` is puur voor de leesbaarheid van de
 * dropdown; het is géén apart keuzeveld. Codes zijn 1-op-1 TI-codes en deels alfanumeriek
 * (PO, KZ, S4), dus behandel ze altijd als string.
 */
export const MONSTER_VERPAKKING: ReadonlyArray<{ code: string; label: string; group: string }> = [
  { group: 'Pot',       code: '50', label: 'Glazen pot 580 ml' },
  { group: 'Pot',       code: '51', label: 'Glazen pot 1060 ml' },
  { group: 'Pot',       code: 'PO', label: 'Kunststof pot' },
  { group: 'Pot',       code: '49', label: 'PFAS-pot' },
  { group: 'Pot',       code: 'SP', label: 'Slibpot' },
  { group: 'Fles',      code: '90', label: 'Bruine glazen fles 500 ml' },
  { group: 'Fles',      code: '89', label: 'Bruine glazen fles 1 l' },
  { group: 'Fles',      code: '92', label: 'PE-fles 250 ml' },
  { group: 'Fles',      code: '91', label: 'PE-fles 1 l' },
  { group: 'Vluchtig',  code: '87', label: 'Headspace bruin glas' },
  { group: 'Vluchtig',  code: '94', label: 'Headspace transparant glas' },
  { group: 'Vluchtig',  code: 'VI', label: 'Vial' },
  { group: 'Vluchtig',  code: '98', label: 'Methanol kit' },
  { group: 'Bulk',      code: 'KZ', label: 'Kunststofzak' },
  // Emmer zonder maat: wat de veldwerker zegt als hij de inhoud niet noemt. Staat vóór de
  // maten, want het is de algemene keuze. ⚠ 'EM' is een INTERNE code — zie VERPAKKING_TI.
  { group: 'Bulk',      code: 'EM', label: 'Emmer' },
  { group: 'Bulk',      code: 'E8', label: 'Emmer 1,8 l' },
  { group: 'Bulk',      code: '76', label: 'Emmer 3 l' },
  { group: 'Bulk',      code: 'E5', label: 'Emmer 5 l' },
  { group: 'Bulk',      code: 'E1', label: 'Emmer 10 l' },
  { group: 'Bulk',      code: '99', label: 'Emmer asbest' },
  { group: 'Ongeroerd', code: 'S4', label: 'Steekbus 40 mm' },
  { group: 'Ongeroerd', code: '71', label: 'Steekbus 50 mm' },
  { group: 'Ongeroerd', code: 'S6', label: 'Steekbus 67 mm' },
  { group: 'Ongeroerd', code: '82', label: 'Steekbus 100 mm' },
  { group: 'Ongeroerd', code: '70', label: 'Steekring' },
  { group: 'Ongeroerd', code: 'KO', label: 'Kopecky-ring' },
  { group: 'Ongeroerd', code: '79', label: 'PVC-liner 70 mm' },
  { group: 'Ongeroerd', code: '80', label: 'PVC-liner 100 mm' },
  { group: 'Ongeroerd', code: '81', label: 'PVC-liner 110 mm' },
  { group: 'Overig',    code: '58', label: 'Anders' },
];

/** Default-verpakking voor een nieuw grondmonster. */
export const DEFAULT_MONSTER_VERPAKKING = '50';

/**
 * Verpakkingscode → TerraIndex `JrPackingTypeCode`.
 *
 * De codes in de lijst hierboven zijn 1-op-1 TI-codes; alleen waar wij een keuze toevoegen die
 * TerraIndex niet als zodanig kent, staat hier een vertaling. Zolang een interne code hier geen
 * geverifieerde TI-code tegenover heeft, gaat het veld LEEG mee in plaats van met een verzonnen
 * code: een leeg veld is te herstellen, een verkeerd type verpakking bij een monster niet.
 */
export const VERPAKKING_TI: Record<string, string | null> = {
  EM: null, // ⚠ 'Emmer' zonder maat — TI-code nog te bevestigen
};

/** De TI-code voor de export, of null als we die (nog) niet weten. */
export function verpakkingToTi(code: string | null | undefined): string | null {
  if (!code) return null;
  const c = String(code);
  return c in VERPAKKING_TI ? VERPAKKING_TI[c] : c;
}

const VERPAKKING_BY_CODE = new Map(MONSTER_VERPAKKING.map((v) => [v.code, v]));
/** Leesbaar label bij een verpakkingscode (valt terug op de code zelf). */
export function monsterVerpakkingLabel(code: string | null | undefined): string {
  if (!code) return '';
  return VERPAKKING_BY_CODE.get(String(code))?.label ?? String(code);
}

// ── Watermonster (peilbuis-watermonster) ────────────────────────────────────
/** Opbrengst peilbuis — TI WsYieldCode. */
export const WATERMONSTER_OPBRENGST: readonly string[] = ['goed', 'matig', 'slecht'];
/** Transparantie/helderheid — NEN 6693 §11.3: precies drie klassen (TI WsClarityCode). */
export const WATERMONSTER_HELDERHEID: readonly string[] = ['helder', 'half doorzichtig', 'ondoorzichtig'];
/**
 * Pomptype monstername — korte lijst (TI WsPumpTypeCode kent er 22; bewust ingekort).
 * 'slangenpomp' is de veldnaam van de peristaltische pomp; heette tot 2026-10 'peristaltische pomp'.
 */
export const WATERMONSTER_POMPTYPE: readonly string[] = [
  'slangenpomp (peristaltisch)', 'centrifugaalpomp', 'membraanpomp', 'handpomp/zuigerpomp', 'bailer', 'overig',
];
/**
 * Pomptype -> TerraIndex `WsPumpTypeCode` (codelijst 165).
 *
 * Onze lijst is korter dan die van TerraIndex en dekt hem niet helemaal: 'centrifugaalpomp',
 * 'membraanpomp' en 'overig' hebben daar geen tegenhanger. Die staan hier bewust niet in --
 * dan blijft het veld bij export leeg in plaats van dat er een pomp in het rapport komt die de
 * monsternemer niet gebruikt heeft.
 *
 * 'peristaltische pomp' is de oude naam van 'slangenpomp (peristaltisch)'. Hij staat niet meer
 * in de keuzelijst, maar blijft hier zodat bestaande watermonsters met die tekst goed exporteren.
 */
export const WATERMONSTER_POMPTYPE_TI = new Map<string, string>([
  ['slangenpomp (peristaltisch)', '1'],
  ['peristaltische pomp', '1'],
  ['handpomp/zuigerpomp', '3'],
  ['bailer', '12'],
]);
/** Oude pomptype-namen -> huidige keuzelijstwaarde, zodat een keuzelijst een opgeslagen oude waarde herkent. */
export const WATERMONSTER_POMPTYPE_ALIASES = new Map<string, string>([
  ['peristaltische pomp', 'slangenpomp (peristaltisch)'],
]);

/**
 * Matrix — de opties van TerraIndex plus 'oppervlaktewater' (gevraagd door veldwerkers.nl).
 * 'grondwater' blijft de standaard voor een nieuw watermonster.
 */
export const WATERMONSTER_MATRIX: readonly string[] = [
  'afvalwater', 'grond', 'grondwater', 'overig', 'waterbodem', 'oppervlaktewater',
];
/**
 * Matrix -> TerraIndex `WsMatrixCode`.
 *
 * Uit de formulierdefinitie van TerraIndex (TI_waterSampleDetails.json, veld WsMatrixCode: daar
 * is de waarde gelijk aan de code). 'GW' is bovendien bevestigd in een echte TI-export. Het veld
 * is in TerraIndex verplicht; leeg laten maakte van elk ander monster stilletjes grondwater.
 */
export const WATERMONSTER_MATRIX_TI = new Map<string, string>([
  ['grondwater', 'GW'],
  ['afvalwater', 'AW'],
  ['grond', 'GR'],
  ['overig', 'OV'],
  ['waterbodem', 'SL'],
  ['oppervlaktewater', 'OW'],
]);
/** Filtertype fles — default 0,45 µm membraanfilter. */
export const WATERBOTTLE_FILTERTYPE: readonly string[] = ['0,45 µm membraanfilter', '0,2 µm membraanfilter', 'anders'];
/** Flestype — korte lijst (niet de 96 opties van TI JrPackingTypeCode). */
export const WATERBOTTLE_TYPE: readonly string[] = [
  'glas donker', 'glas helder', 'glas met septum (headspace-vrij)', 'kunststof (PE/HDPE)', 'overig',
];

/**
 * MpStopCriterionCode — reden boring gestopt (TerraIndex stop-criterion list).
 * Codes reverse-engineered from a TerraIndex .ti export (they are NOT sequential).
 */
export const STOP_CRITERIUM = new Map<string, string>([
  ['Beperking technisch', '1'],
  ['Einddoel', '2'],
  ['Hout', '28'],
  ['Kabels en leidingen', '19'],
  ['Massieve laag', '17'],
  ['Obstakel constructie', '3'],
  ['Obstakel grind stenen', '4'],
  ['Obstakel ijzervloer', '5'],
  ['Obstakel onbekend', '6'],
  ['Obstakel puin', '7'],
  ['Obstakel vast gesteente', '8'],
  ['Onbekend', '14'],
  ['Risico', '9'],
  ['Risico grondwaterdruk', '10'],
  ['Risico kabels of leidingen', '16'],
  ['Storing organisatorisch', '11'],
  ['Storing technisch', '12'],
  ['Water aangetroffen', '15'],
  ['Werkwaterverlies', '13'],
]);

/**
 * LaOWTypeCode — olie/water-reactie van een laag (TerraIndex).
 * Codes reverse-engineered from a TerraIndex .ti export.
 */
export const OLIE_WATER_REACTIE = new Map<string, string>([
  ['Geen', '0'],
  ['Zwak', '1'],
  ['Matig', '2'],
  ['Sterk', '3'],
  ['Zeer sterk', '4'],
]);

/**
 * LaConsistencyCode — consistentie van een laag (TerraIndex codelijst 48). In de formulierdefinitie
 * heet het veld LaSoilConsistency, maar in de XML schrijft en leest TerraIndex LaConsistencyCode.
 *
 * Term → TI-waarde, letterlijk overgenomen uit de formulierdefinitie van TerraIndex
 * (TI_grondlaag.json, veld LaSoilConsistency, codeListLookupId 48). De volgorde van de codes is
 * historisch en niet oplopend met de schaal; daarom staat hij hier per term vast en rekenen we
 * er nergens mee.
 *
 * Sleutel = de term zoals wij hem opslaan: kleine letters, zoals NEN 6693 hem schrijft. De twee
 * normen delen deze lijst. Wat NEN 6693 kent en TerraIndex niet -- de rijpings- en slibklassen
 * (ongerijpt, steekvast slib, …) -- staat er bewust niet in: dat gaat bij export leeg mee in
 * plaats van dat we het op een klasse gooien die iets anders betekent.
 */
export const CONSISTENTIE = new Map<string, string>([
  ['uiterst slap', '5'],
  ['zeer slap', '4'],
  ['slap', '1'],
  ['matig slap', '6'],
  ['matig stevig', '2'],
  ['stevig', '7'],
  ['vast', '3'],
  ['stijf', '8'],
  ['zeer stijf', '9'],
]);

/**
 * Type onderzoek van een boring (`Boring.type`) — de enige bron voor die keuzelijst.
 *
 * Hier stond eerst tweemaal hetzelfde, en de twee waren het oneens. De backoffice koos uit
 * `BORING_TYPES` (kale labels) en bewaarde het LABEL; de veldapp koos uit `MEETPUNT_TYPE`
 * (label→code) en bewaarde de CODE — in dezelfde kolom, met deels andere waarden. Wie een
 * boring in de app aanmaakte en in de backoffice opende, zag zijn keuze niet terug.
 *
 * Daarom één lijst met drie kolommen, zelfde vorm als BOORSYSTEEM: `code` is wat wij
 * opslaan, `label` is wat de gebruiker leest, `ti` is de TerraIndex-code (MpTypeCode).
 * Losse codes en niet de TI-code als sleutel, omdat TerraIndex handboring/pulsboring/
 * edelmanboring niet kent -- dat zijn boormethodes, geen punttypen -- en die dus alle drie
 * op 'B' uitkomen. Eén sleutel per rij is meer waard dan een sleutel die toevallig gelijk is.
 *
 * TI-codes geverifieerd tegen de TerraIndex-codelijst (`TI_boringMeetpunt.json`,
 * frmMeasurementPointDetails → MpTypeCode) en tegen een echte export waarin een sleuf
 * MpTypeCode 35 draagt en een gewone boring 'B'.
 */
export const BORING_TYPE: ReadonlyArray<{ code: string; label: string; ti: string }> = [
  { code: 'BO', label: 'Boring', ti: 'B' },
  { code: 'PB', label: 'Peilbuis', ti: 'P' },
  { code: 'GG', label: 'Graafgat', ti: 'A' },
  { code: 'SO', label: 'Sondering', ti: 'C' },
  // TerraIndex kent pulsboring niet als punttype (het is een boormethode); bij export 'boring'.
  { code: 'PU', label: 'Pulsboring', ti: 'B' },
  { code: 'IB', label: 'Inspectiegat met boring', ti: '35' },
  { code: 'WM', label: 'Watermonster', ti: 'J' },
];

/**
 * Typen die er niet meer in staan, en wat ze nu zijn. Handboring, edelmanboring en inspectieput
 * zijn in de praktijk allemaal een gewone boring (de methode hoort bij het boorsysteem van een
 * laag). Ze stonden in de keuzelijst en er staan boringen op; die lezen we voortaan als 'BO',
 * in elk scherm en in elke export. Geen datamigratie nodig: de kolom houdt de oude waarde tot
 * iemand de boring opslaat.
 */
export const LEGACY_BORING_TYPE: ReadonlyMap<string, string> = new Map([
  ['HB', 'BO'], ['HANDBORING', 'BO'],
  ['ED', 'BO'], ['EDELMANBORING', 'BO'],
  ['IP', 'BO'], ['INSPECTIEPUT', 'BO'],
]);

/**
 * Typen met een uitgegraven gat: inspectiegat met boring en graafgat. Daar horen de sleuflengte
 * en -breedte en de zeefmassa's (> 20 mm / < 20 mm) bij, en daar tonen veldapp en backoffice die
 * velden. Bij een gewone boring, peilbuis of sondering valt er niets te meten -- een breedte in
 * de opname is daar altijd een maat van iets anders.
 */
export const BORING_TYPES_WITH_PIT_DIMENSIONS: ReadonlySet<string> = new Set(['IB', 'GG']);

/**
 * Mag dit boringtype een sleuflengte/-breedte dragen?
 *
 * Het extractiemodel plakt een omgevingsmaat er soms op: bij boring M01 ("ballast breedte 4
 * meter") belandde de breedte van het ballastbed als MpTrialPitWidth in TerraIndex. Een
 * promptregel helpt daar wel tegen, maar blijft een kwestie van kans; dit is de harde grens.
 *
 * Een ONBEKEND type levert `false` op. Eerder stond hier `true`, met als argument dat het type
 * lang niet altijd wordt ingesproken en een weggegooide sleufmaat veldwerk kost. Dat argument
 * bleek de verkeerde kant op te wegen: in het formulier verschijnen deze velden pas bij een
 * inspectiegat, dus een maat op een boring zonder type is een waarde die je met de hand nooit
 * had kunnen invullen — onzichtbaar, niet te corrigeren, en wél zichtbaar in de export en in de
 * validatiediff. En het kost weinig: wie een sleuf opmeet, zégt "sleuf", en daar leidt het model
 * het type IB al uit af.
 */
export function acceptsPitDimensions(value?: string | null): boolean {
  const code = normalizeBoringType(value);
  return !!code && BORING_TYPES_WITH_PIT_DIMENSIONS.has(code);
}

/**
 * De sleufmaten zoals ze voor dit type opgeslagen MOGEN worden: bij een type dat geen gat is,
 * allebei leeg. Eén plek waar die regel wordt toegepast, zodat elk schrijfpad (AI-verwerking,
 * handmatige bewerking, API) dezelfde uitkomst heeft.
 */
export function pitDimensionsFor(
  type: string | null | undefined,
  dims: { lengthM?: number | null; widthM?: number | null },
): { lengthM: number | null; widthM: number | null } {
  if (!acceptsPitDimensions(type)) return { lengthM: null, widthM: null };
  return { lengthM: dims.lengthM ?? null, widthM: dims.widthM ?? null };
}

/**
 * Grondwater: de diepte en de ja/nee-vraag horen bij elkaar.
 *
 * In het formulier verschijnt "diepte grondwaterstand" pas als 'Grondwater aangetroffen?' op Ja
 * staat. Een diepte zonder dat Ja is dus onzichtbaar: het extractiemodel levert alleen een
 * diepte ("grondwaterstand is 4 meter 20") en kent de vraag niet eens, en zo verdween een
 * ingesproken meting uit beeld.
 *
 * Een diepte IS het antwoord op die vraag — je meet niets wat er niet is — dus die wint: is er
 * een diepte, dan is 'aangetroffen' waar. Andersom hoort bij een expliciet 'nee' geen diepte.
 */
export function groundwaterFor(
  found: boolean | null | undefined,
  cmMv: number | null | undefined,
): { found: boolean | null; cmMv: number | null } {
  if (cmMv != null) return { found: true, cmMv };
  return { found: found ?? null, cmMv: null };
}

export const BORING_TYPE_INSPECTIEGAT = 'IB';

const _boringTypeByCode = new Map(BORING_TYPE.map(t => [t.code.toUpperCase(), t]));
const _boringTypeByLabel = new Map(BORING_TYPE.map(t => [t.label.toLowerCase(), t]));

/**
 * Maak van een opgeslagen waarde een `BORING_TYPE`-code.
 *
 * Accepteert alle drie de vormen die er in de kolom staan: de nieuwe code ('IB'), het oude
 * backoffice-label ('Boring') en de oude veldapp-code — die op 'BO'/'GG'/'SO'/'IP'/'WM' al
 * gelijk is. Zo hoeft er geen datamigratie overheen: wat er staat blijft leesbaar, en wat er
 * nieuw bij komt is eenduidig. Onbekend → null, zodat een tikfout niet stilletjes 'Boring' wordt.
 */
export function normalizeBoringType(value?: string | null): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  const legacy = LEGACY_BORING_TYPE.get(v.toUpperCase());
  if (legacy) return legacy;
  return (_boringTypeByCode.get(v.toUpperCase()) ?? _boringTypeByLabel.get(v.toLowerCase()))?.code ?? null;
}

/** Leesbaar label bij een opgeslagen type. Valt terug op de ruwe waarde, nooit op niets. */
export function getBoringTypeLabel(value?: string | null): string | null {
  const code = normalizeBoringType(value);
  return code ? _boringTypeByCode.get(code)!.label : (value ?? null);
}

/** TerraIndex MpTypeCode bij een opgeslagen type, of null als het type onbekend is. */
export function boringTypeToTi(value?: string | null): string | null {
  const code = normalizeBoringType(value);
  return code ? _boringTypeByCode.get(code)!.ti : null;
}

/**
 * TerraIndex MpTypeCode, met de afleiding als terugval.
 *
 * Boringen van vóór dit veld hebben geen type. Die kregen tot nu toe een AFGELEIDE code: mét
 * peilbuis 'P', zonder 'B'. Dat raadt goed voor de twee gevallen die we tot dan toe hadden,
 * en het is beter dan een leeg MpTypeCode -- daar kan TerraIndex niets mee.
 *
 * Staat er wél een type, dan wint dat altijd: de veldwerker heeft dan iets gekozen, en een
 * afleiding die zijn keuze overstemt is erger dan geen afleiding.
 */
export function boringTypeToTiOrDerived(value: string | null | undefined, hasPeilbuis: boolean): string {
  return boringTypeToTi(value) ?? (hasPeilbuis ? 'P' : 'B');
}

/** True als dit precies 'Inspectiegat met boring' is. Voor de sleufvelden: hasPitFields. */
export function isInspectiegatType(value?: string | null): boolean {
  return normalizeBoringType(value) === BORING_TYPE_INSPECTIEGAT;
}

/**
 * Toont dit type de sleufvelden (sleuflengte/-breedte, zeefmassa's > 20 mm en < 20 mm)?
 * Inspectiegat met boring en graafgat. De ene regel voor veldapp, backoffice en backend.
 */
export function hasPitFields(value?: string | null): boolean {
  return acceptsPitDimensions(value);
}

/**
 * Het type bij een INGESPROKEN boring, gegeven de sleufgegevens die het model eruit haalde.
 *
 * Spreekt iemand een sleufmaat of een zeefmassa in, maar noemt hij geen type dat die velden
 * toont (of helemaal geen type), dan werden de waarden wel opgeslagen maar waren ze in de
 * veldapp en de backoffice onzichtbaar -- en dus niet te controleren of te verbeteren. Wie een
 * sleuf opmeet of zeeft, werkt in een inspectiegat: dan wordt het type 'Inspectiegat met boring'.
 * Een graafgat blijft een graafgat (dat toont dezelfde velden).
 */
export function boringTypeForPitData(
  type: string | null | undefined,
  data: { lengthM?: number | null; widthM?: number | null; massTotalKg?: number | null; massGt20Kg?: number | null; massLt20Kg?: number | null },
): string | null {
  const code = normalizeBoringType(type);
  const heeftSleufData = [data.lengthM, data.widthM, data.massTotalKg, data.massGt20Kg, data.massLt20Kg].some((v) => v != null);
  if (!heeftSleufData || hasPitFields(code)) return code ?? (type ?? null);
  return BORING_TYPE_INSPECTIEGAT;
}


/** Grouped export — matches the original tiCodelists shape exactly for drop-in compatibility. */
export const tiCodelists = {
  bodemsoort: BODEMSOORT,
  // Voor code ↔ naam bij bestaande lagen (bevat ook keien); niet voor keuzelijsten of AI-enums.
  bodemsoortHistorisch: BODEMSOORT_HISTORISCH,
  toevoeging: TOEVOEGING,
  gradatie: GRADATIE,
  mediaan: MEDIAAN,
  kleur: KLEUR,
  kleurSterkte: KLEUR_STERKTE,
  geurType: GEUR_TYPE,
  geurSterkte: GEUR_STERKTE,
  meetpuntType: MEETPUNT_TYPE,
  boorsysteem: BOORSYSTEEM,
  groundLevelType: MAAIVELD_TYPE,
  // BZB — original key names preserved so existing backend code keeps working
  LaSpecialCompounds: BZB_TYPE,
  LaSpecialCompoundsGradiation: BZB_GRADATION,
  LaSpecialCompoundsOrigin: BZB_ORIGIN,
  // Peilbuis
  peilbuisMateriaal: PEILBUIS_MATERIAAL,
  peilbuisDoorloop: PEILBUIS_DOORLOOP,
  peilbuisReferentievlak: PEILBUIS_REFERENTIEVLAK,
  peilbuisRefTop: PEILBUIS_REF_TOP,
  // Boring
  stopCriterium: STOP_CRITERIUM,
  // Layer
  olieWaterReactie: OLIE_WATER_REACTIE,
  consistentie: CONSISTENTIE,
} as const;
